import React, { useEffect, useMemo, useState } from "react";
import {
  useListOrders,
  type Order,
  type ListOrdersParams,
} from "@workspace/api-client-react";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";
import {
  Search,
  Loader2,
  X,
  Hash,
  Package,
  MapPin,
  Building2,
  Phone,
  Mail,
  Calendar,
  CheckCircle2,
  Tag,
  User,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const LEAD_STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "All statuses", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "In progress", value: "in_progress" },
  { label: "Action required", value: "action_required" },
  { label: "Installed", value: "installed" },
  { label: "Active", value: "active" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Rejected", value: "rejected" },
];

const PAGE_SIZE = 25;

function prettyLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function LeadsExplorer() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Order | null>(null);

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const params: ListOrdersParams = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(search ? { search } : {}),
      ...(status !== "all" ? { status: status as ListOrdersParams["status"] } : {}),
    }),
    [page, search, status],
  );

  const { data, isLoading, isFetching, error } = useListOrders(params);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-[#0B132B]">Leads</h1>
        <p className="text-sm text-gray-500 mt-1">
          All Openserve leads imported across the network.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by lead ID, customer, reference, region, dealer, contact…"
            className="pl-9 bg-white"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="text-sm border border-gray-200 rounded-md py-2 px-3 bg-white focus:outline-none focus:border-[#E6007E]"
        >
          {LEAD_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
              <tr>
                <th className="px-4 py-3">Lead ID</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Package</th>
                <th className="px-4 py-3">Region</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Captured</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                    <Loader2 size={18} className="inline animate-spin mr-2" />
                    Loading leads…
                  </td>
                </tr>
              )}
              {error && !isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-red-500 text-sm">
                    Failed to load leads.
                  </td>
                </tr>
              )}
              {!isLoading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-400 text-sm">
                    No leads match your filters.
                  </td>
                </tr>
              )}
              {!isLoading &&
                !error &&
                items.map((o) => (
                  <tr
                    key={o.id}
                    onClick={() => setSelected(o)}
                    className="group hover:bg-gray-50/70 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs font-semibold text-[#0B132B] underline-offset-2 group-hover:text-[#E6007E] group-hover:underline">
                        {o.externalLeadId ?? o.reference}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-[#0B132B]">
                      {o.customerName}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {o.agentName ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{o.package}</td>
                    <td className="px-4 py-3 text-gray-600">{o.region ?? "—"}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {fmtDate(o.dateCaptured ?? o.submittedAt)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
          <span className="text-xs text-gray-500 flex items-center gap-2">
            {isFetching && <Loader2 size={13} className="animate-spin" />}
            {total.toLocaleString("en-ZA")} lead{total === 1 ? "" : "s"}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 text-sm border border-gray-200 rounded-md disabled:opacity-40 hover:bg-white"
            >
              Prev
            </button>
            <span className="text-xs text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 text-sm border border-gray-200 rounded-md disabled:opacity-40 hover:bg-white"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <LeadDetailSheet lead={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

function LeadDetailSheet({
  lead,
  onClose,
}: {
  lead: Order | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={Boolean(lead)} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        {lead && (
          <>
            <SheetHeader className="text-left">
              <SheetTitle className="text-[#0B132B] flex items-center justify-between gap-3">
                <span className="truncate">{lead.customerName}</span>
                <button
                  onClick={onClose}
                  className="shrink-0 text-gray-400 hover:text-[#0B132B]"
                >
                  <X className="w-4 h-4" />
                </button>
              </SheetTitle>
            </SheetHeader>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <StatusBadge status={lead.status} />
                <span className="text-lg font-bold text-[#0B132B]">
                  {formatZAR(lead.priceCents)}
                </span>
              </div>
              <div className="rounded-xl border border-gray-100 divide-y divide-gray-50">
                <Row icon={Hash} label="Lead ID" value={lead.externalLeadId ?? lead.reference ?? "—"} />
                <Row icon={Hash} label="Reference" value={lead.reference} />
                <Row icon={Package} label="Package" value={lead.package} />
                <Row icon={User} label="Agent" value={lead.agentName ?? "—"} />
                {lead.leadStatus && (
                  <Row icon={Tag} label="Lead status" value={prettyLabel(lead.leadStatus)} />
                )}
                {lead.leadType && (
                  <Row icon={Tag} label="Lead type" value={prettyLabel(lead.leadType)} />
                )}
                <Row icon={MapPin} label="Address" value={lead.address ?? "—"} />
                {lead.region && <Row icon={MapPin} label="Region" value={lead.region} />}
                {lead.isp && <Row icon={Package} label="ISP" value={lead.isp} />}
                {lead.dealer && <Row icon={Building2} label="Dealer" value={lead.dealer} />}
                {lead.contactNumber && (
                  <Row icon={Phone} label="Contact" value={lead.contactNumber} />
                )}
                {lead.contactEmail && (
                  <Row icon={Mail} label="Email" value={lead.contactEmail} />
                )}
                {lead.capturedByEmail && (
                  <Row icon={User} label="Captured by" value={lead.capturedByEmail} />
                )}
                {lead.dateCaptured && (
                  <Row icon={Calendar} label="Captured" value={fmtDate(lead.dateCaptured)} />
                )}
                {lead.externalLastUpdated && (
                  <Row
                    icon={Calendar}
                    label="Last updated"
                    value={fmtDate(lead.externalLastUpdated)}
                  />
                )}
                {lead.installedAt && (
                  <Row
                    icon={CheckCircle2}
                    label="Installed"
                    value={fmtDate(lead.installedAt)}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
}) {
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <Icon className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-sm text-gray-500 w-28 shrink-0">{label}</span>
      <span className="text-sm font-medium text-[#0B132B] text-right flex-1 break-words">
        {value}
      </span>
    </div>
  );
}
